import { IDesc, IField } from 'app/services/api5-service/api.interface';
import { Observable, combineLatest, of } from 'rxjs';
import { map, shareReplay, switchMap } from 'rxjs/operators';

import { ISPFormState } from 'common/dynamic-form/model';
import { DocHelper } from 'utils/dochelper';

import { FieldDesc, FieldDescItem } from './model';

import { FieldSettings } from '../model';

/**
 * Get whole description hidden state
 *
 * @param descItems
 */
function getFieldDescHidden$(descItems: FieldDescItem[]): Observable<boolean> {
  if (descItems.length === 0) {
    return of(true);
  }

  return combineLatest(descItems.map(desc => desc.hidden$)).pipe(
    map(states => states.every(isHidden => isHidden)),
  );
}

/**
 * Get description item for field
 *
 * @param desc
 * @param state
 */
function getFieldDescItem(desc: IDesc, state: ISPFormState): FieldDescItem {
  return {
    text: DocHelper.getMessage(desc.$name, state.docSubject.value),
    color: desc.$color,
    hidden$: state.hiddenService.isHidden$(desc.$name),
  };
}

/**
 * Get desc from field metadata
 *
 * @param field
 * @param state
 */
export function getDesc(
  field: IField,
  state: ISPFormState,
): FieldDesc | undefined {
  // checkbox render description by it's own rules
  if (!field.desc) {
    return;
  }

  const descItems = field.desc.map(desc => getFieldDescItem(desc, state));

  const fieldDesc: FieldDesc = {
    msg: descItems.map(desc => desc.text).join(' '),
    position: field.desc.some(desc => desc.$style === 'plainhint')
      ? 'under'
      : 'label',
    descItems,
    hidden$: getFieldDescHidden$(descItems),
  };

  return fieldDesc;
}

/**
 * Get desc for field component
 *
 * @param field
 * @param state - form state
 * @param fieldSettings
 */
export function getFieldDesc(
  field: IField,
  state: ISPFormState,
  fieldSettings?: FieldSettings,
): FieldDesc | undefined {
  const fieldDesc = getDesc(field, state);

  if (fieldSettings?.hideDesc) {
    return;
  }

  return fieldDesc;
}

/**
 * Get stream of desc for field
 *
 * @param field
 * @param state
 * @param fieldSettings$
 */
export function getFieldDesc$(
  field: IField,
  state: ISPFormState,
  fieldSettings$: Observable<FieldSettings | undefined>,
): Observable<FieldDesc | undefined> {
  return combineLatest([fieldSettings$, state.docSubject]).pipe(
    switchMap(([fieldSettings]) => {
      if (fieldSettings?.customDesc$) {
        return fieldSettings.customDesc$;
      }

      return of(getFieldDesc(field, state, fieldSettings));
    }),
    shareReplay({
      refCount: true,
      bufferSize: 1,
    }),
  );
}

/**
 * Get stream of desc for input field
 *
 * @param field
 * @param state
 */
export function getDesc$(
  field: IField,
  state: ISPFormState,
): Observable<FieldDesc | undefined> {
  return state.docSubject.pipe(
    map(() => getDesc(field, state)),
    shareReplay({
      refCount: true,
      bufferSize: 1,
    }),
  );
}
